import * as React from 'react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
    BooleanField,
    DatagridConfigurable,
    ExportButton,
    FieldProps,
    Link,
    List,
    NumberField,
    SelectColumnsButton,
    TextField,
    TopToolbar,
    useGetOne,
    useListContext,
    useRecordContext,
} from 'react-admin';
import { Divider, Tabs, Tab, Tooltip, Modal } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { LoadingSection } from '../../common/LoadingSection';
import Button from '@mui/material/Button';
import {
    Description,
    Videocam,
    PictureAsPdf,
    Edit,
    DocumentScanner,
    Close,
} from '@mui/icons-material';
import { Contents, Tests } from '../../custom';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import StatusTags from '../../common/StatusTags';
import { ShowCommentsButton } from '../../common/ShowCommentsButton';
import { BulkUploadSectionQuestions } from '../sections/SectionQuestionUpload';
import { ChevronRightIcon } from 'lucide-react';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import QuestionPaper from '../../common/question-paper/QuestionPaperView';

const ListActions = () => {
    const { subjectId, programId, topicId, chapterId } = useParams();
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <Button
                startIcon={<Videocam />}
                onClick={() => {
                    navigate(
                        `/programs/${programId}/subjects/${subjectId}/chapters/${chapterId}/topics/${topicId}/contents/create?type=LECTURES`,
                    );
                }}
                sx={{ padding: '0 5px' }}
            >
                Add a Video
            </Button>
            <Button
                startIcon={<PictureAsPdf />}
                onClick={() => {
                    navigate(
                        `/programs/${programId}/subjects/${subjectId}/chapters/${chapterId}/topics/${topicId}/contents/create?type=NOTES`,
                    );
                }}
                sx={{ padding: '0 5px' }}
            >
                Add a Note
            </Button>
            <Button
                startIcon={<Description />}
                onClick={() => {
                    navigate(
                        `/programs/${programId}/subjects/${subjectId}/chapters/${chapterId}/topics/${topicId}/contents/create?type=EXERCISES`,
                    );
                }}
                sx={{ padding: '0 5px' }}
            >
                Add an Exercise
            </Button>
        </TopToolbar>
    );
};

const ContentList = () => {
    const { subjectId, programId, topicId, chapterId } = useParams();
    if (!topicId) {
        return <LoadingSection />;
    }

    return (
        <List
            sort={{ field: 'displayOrder', order: 'ASC' }}
            title={<ContentTitle />}
            filterDefaultValues={{
                type: 'LECTURES',
                programId: programId,
                chapterId: chapterId,
                subjectId: subjectId,
                topicId: topicId,
            }}
            perPage={25}
            resource={'contents'}
            filters={contentFilters}
            actions={<ListActions />}
        >
            <TabbedDatagrid />
        </List>
    );
};

const ContentTitle = React.memo(() => {
    const { programId, subjectId, chapterId, topicId } = useParams();
    const { data: chapter, isLoading: loadingChapter } = useGetOne('chapters', { id: chapterId });
    const { data: topic, isLoading: loadingTopic } = useGetOne('topics', { id: topicId });

    if (loadingChapter || loadingTopic) return <span>...</span>;

    return (
        <div className="flex flex-row items-center text-lg font-semibold">
            <Link to="/programs" className="text-blue-600 hover:underline">
                ...
            </Link>
            <ChevronRightIcon />
            <Link to={`/programs/${programId}/subjects`} className="text-blue-600 hover:underline">
                ...
            </Link>
            <ChevronRightIcon />
            <Link
                to={`/programs/${programId}/subjects/${subjectId}/chapters`}
                className="text-blue-600 hover:underline"
            >
                {chapter?.name}
            </Link>
            <ChevronRightIcon />
            <Link
                to={`/programs/${programId}/subjects/${subjectId}/chapters/${chapterId}/topics`}
                className="text-blue-600 hover:underline"
            >
                {topic?.name}
            </Link>

            <ChevronRightIcon />
            <span>Contents</span>
        </div>
    );
});

const contentFilters: any[] = [];

const tabs = [
    { id: 'LECTURES', name: 'Lectures' },
    { id: 'NOTES', name: 'Notes' },
    { id: 'EXERCISES', name: 'Exercises' },
];

const EditButton = () => {
    const navigate = useNavigate();
    const record: Contents = useRecordContext<Contents>();
    const { programId, subjectId, chapterId, topicId } = useParams();
    return (
        <Button
            startIcon={<Edit />}
            onClick={e => {
                navigate(
                    `/programs/${programId}/subjects/${subjectId}/chapters/${chapterId}/topics/${topicId}/contents/${record.id}/edit`,
                );
            }}
        >
            Edit
        </Button>
    );
};

const TabbedDatagrid = () => {
    const listContext = useListContext();
    const { displayedFilters, filterValues, setFilters } = listContext;
    const { subjectId, programId, topicId, chapterId } = useParams();

    useEffect(() => {
        if (subjectId && programId && topicId && chapterId) {
            const cleanFilters = {
                type: filterValues.type,
                programId: programId,
                chapterId: chapterId,
                subjectId: subjectId,
                topicId: topicId,
            };
            setFilters(cleanFilters, displayedFilters, false);
        }
    }, [subjectId, programId, topicId, chapterId]);
    const handleChange = useCallback(
        (event: React.ChangeEvent<{}>, value: any) => {
            setFilters && setFilters({ ...filterValues, type: value }, displayedFilters, false);
        },
        [displayedFilters, filterValues, setFilters],
    );

    const QuestionPaperViewPage = (props: FieldProps<Tests>) => {
        const record = useRecordContext<any>();
        let { programId } = useParams();
        const [showModal, setShowModal] = useState(false);

        if (!record) {
            return null;
        }

        const handleOpenModal = () => {
            setShowModal(true);
        };

        const handleCloseModal = () => {
            setShowModal(false);
        };

        return (
            <>
                <Tooltip title="Show question paper">
                    <Button onClick={handleOpenModal}>
                        <DocumentScanner />
                    </Button>
                </Tooltip>
                <Modal
                    open={showModal}
                    onClose={handleCloseModal}
                    aria-labelledby="question-paper-modal"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <div className="bg-white rounded-lg shadow-2xl min-w-[75vw] max-w-[80vw] min-h-[90vh] max-h-[95vh] relative flex flex-col">
                        <div className="flex-1 overflow-auto ">
                            {record && <QuestionPaper testId={record.exerciseId} />}
                        </div>
                        <div className="sticky bottom-0 z-40 p-2 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-t flex justify-end">
                            <Button
                                variant="contained"
                                onClick={handleCloseModal}
                                aria-label="Close"
                            >
                                Close <Close />
                            </Button>
                        </div>
                    </div>
                </Modal>
            </>
        );
    };

    QuestionPaperViewPage.defaultProps = {};

    const QuestionsLinkField = (props: FieldProps<Tests>) => {
        const record = useRecordContext<any>();
        let { programId } = useParams();
        if (!record) {
            return null;
        }
        return (
            <Link
                to={`/programs/${programId}/tests/${record.exerciseId}/questions_list`}
            >
                Questions
            </Link>
        );
    };

    return (
        <Fragment>
            <Tabs
                variant="fullWidth"
                centered
                value={filterValues.type}
                indicatorColor="primary"
                onChange={handleChange}
            >
                {tabs.map(choice => (
                    <Tab key={choice.id} label={choice.name} value={choice.id} />
                ))}
            </Tabs>
            <Divider />
            {filterValues.type === 'LECTURES' && (
                <DatagridConfigurable bulkActionButtons={false} omit={['type']}>
                    <TextField source="title" sortable={false} />
                    <StatusTags source="status" label={'Status'} sortable={false} />
                    <NumberField source="displayOrder" sortable sortBy="displayOrder" />
                    <BooleanField source="isDemo" sortable={false} />

                    <FormattedDateTimeField source="createdAt" sortable sortBy="createdAt" />

                    <EditButton />
                    <ShowCommentsButton parentType="CONTENT" />
                    <ConfirmDeleteButton />
                </DatagridConfigurable>
            )}
            {filterValues.type === 'NOTES' && (
                <DatagridConfigurable bulkActionButtons={false} omit={['type']}>
                    <TextField source="title" sortable={false} />
                    <StatusTags source="status" label={'Status'} sortable={false} />
                    <NumberField source="displayOrder" sortable sortBy="displayOrder" />
                    <BooleanField source="isDemo" sortable={false} />
                    <FormattedDateTimeField source="createdAt" sortable sortBy="createdAt" />

                    <EditButton />
                    <ConfirmDeleteButton />
                </DatagridConfigurable>
            )}
            {filterValues.type === 'EXERCISES' && (
                <DatagridConfigurable bulkActionButtons={false} omit={['type']}>
                    <TextField source="title" sortable={false} />
                    <StatusTags source="status" label={'Status'} sortable={false} />
                    <NumberField source="displayOrder" sortable sortBy="displayOrder" />
                    <BooleanField source="isDemo" sortable={false} />
                    <FormattedDateTimeField source="createdAt" sortable sortBy="createdAt" />
                    <QuestionsLinkField />
                    <BulkUploadSectionQuestions type={'exercise'} />

                    <QuestionPaperViewPage />
                    <EditButton />
                    <ConfirmDeleteButton />
                </DatagridConfigurable>
            )}
        </Fragment>
    );
};

export default ContentList;
